class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      FacetFiltersForm.requestId += 1;
      FacetFiltersForm.abortController?.abort();
      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev && !FacetFiltersForm.abortController) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoading(loading) {
    document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    ).forEach((spinner) => spinner.classList.toggle('hidden', !loading));
    document.querySelectorAll('#ProductGridContainer .collection, #ProductCount, #ProductCountDesktop').forEach((element) => {
      element.classList.toggle('loading', loading);
    });
    document.getElementById('ProductGridContainer')?.setAttribute('aria-busy', String(loading));
    FacetFiltersForm.toggleActiveFacets(loading);
  }

  static buildPageURL(searchParams) {
    const current = new URL(window.location.href);
    const url = new URL(current.pathname, current.origin);
    url.search = searchParams;
    url.hash = current.hash;
    const previewParameters = ['preview_theme_id', '_ab', '_fd', '_sc'];
    current.searchParams.forEach((value, key) => {
      if (/^(utm_[a-z_]+|gclid|fbclid|msclkid|q|type|options\[[^\]]+\])$/.test(key) && !url.searchParams.has(key)) {
        url.searchParams.append(key, value);
      }
    });
    previewParameters.forEach((key) => url.searchParams.delete(key));
    if (current.hostname.endsWith('.myshopify.com') && /^\d+$/.test(current.searchParams.get('preview_theme_id') || '')) {
      previewParameters.forEach((key) => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
    }
    const path = `${url.pathname}${url.search}${url.hash}`;
    return new URL(window.DomaPreviewNavigation?.preserveURL(path) || path, current.origin);
  }

  static captureFormState() {
    return Array.from(document.querySelectorAll('facet-filters-form form'), (form) => ({
      id: form.id,
      fields: Array.from(form.elements, (field) => ({ value: field.value, checked: field.checked })),
    }));
  }

  static restoreFormState() {
    FacetFiltersForm.formState.forEach(({ id, fields }) => {
      const form = document.getElementById(id);
      if (!form) return;
      fields.forEach((state, index) => {
        const field = form.elements[index];
        if (!field) return;
        field.value = state.value;
        if (typeof state.checked === 'boolean') field.checked = state.checked;
      });
      form.querySelectorAll('price-range').forEach((range) => range.setMinAndMaxValues());
    });
  }

  static showError(show) {
    let error = document.getElementById('FacetFiltersError');
    if (!error && show) {
      error = document.createElement('p');
      error.id = 'FacetFiltersError';
      error.className = 'product-count__text';
      error.setAttribute('role', 'alert');
      document.getElementById('ProductGridContainer')?.prepend(error);
    }
    if (error) {
      error.textContent = show ? window.facetStrings.error : '';
      error.hidden = !show;
    }
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    const requestId = ++FacetFiltersForm.requestId;
    FacetFiltersForm.abortController?.abort();
    const controller = new AbortController();
    FacetFiltersForm.abortController = controller;
    FacetFiltersForm.setLoading(true);
    FacetFiltersForm.showError(false);
    try {
      const pageURL = FacetFiltersForm.buildPageURL(searchParams);
      const sections = FacetFiltersForm.getSections();
      const responses = await Promise.all(sections.map(async (section) => {
        const url = new URL(pageURL);
        url.hash = '';
        url.searchParams.set('section_id', section.section);
        const key = url.toString();
        const cached = FacetFiltersForm.filterData.find((entry) => entry.url === key);
        const html = cached ? cached.html : await FacetFiltersForm.renderSectionFromFetch(key, controller.signal);
        FacetFiltersForm.validateSectionHTML(html, section.section);
        return { html, url: key };
      }));
      if (requestId !== FacetFiltersForm.requestId) return;
      responses.forEach(({ html, url }) => {
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.filterData = FacetFiltersForm.filterData.filter((entry) => entry.url !== url);
        FacetFiltersForm.filterData.push({ html, url });
      });
      const appliedSearchParams = pageURL.search.slice(1);
      if (updateURLHash) FacetFiltersForm.updateURLHash(appliedSearchParams);
      FacetFiltersForm.searchParamsPrev = appliedSearchParams;
      FacetFiltersForm.formState = FacetFiltersForm.captureFormState();
      FacetFiltersForm.appliedURL = `${pageURL.pathname}${pageURL.search}${pageURL.hash}`;
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
    } catch {
      if (requestId !== FacetFiltersForm.requestId) return;
      FacetFiltersForm.restoreFormState();
      if (!updateURLHash) {
        history.replaceState({ ...history.state, searchParams: FacetFiltersForm.searchParamsPrev }, '', FacetFiltersForm.appliedURL);
      }
      FacetFiltersForm.showError(true);
    } finally {
      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.abortController = null;
        FacetFiltersForm.setLoading(false);
      }
    }
  }

  static async renderSectionFromFetch(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok || !/^text\/html(?:;|$)/i.test(response.headers.get('content-type') || '')) {
      throw new Error('Invalid facet response');
    }
    if (response.url && new URL(response.url).origin !== window.location.origin) {
      throw new Error('Unexpected facet response origin');
    }
    return response.text();
  }

  static validateSectionHTML(html, sectionId) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsed.getElementById('ProductGridContainer');
    if (!grid?.querySelector('.collection') || grid.querySelector('#product-grid')?.dataset.id !== sectionId) {
      throw new Error('Missing facet grid');
    }
    ['ProductCount', 'ProductCountDesktop', 'FacetFiltersForm', 'FacetFiltersFormMobile', 'FacetSortForm', 'FacetSortDrawerForm'].forEach((id) => {
      if (document.getElementById(id) && !parsed.getElementById(id)) throw new Error('Missing facet fragment');
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const count = (parsed.getElementById('ProductCount') || parsed.getElementById('ProductCountDesktop'))?.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container && count !== undefined) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop && count !== undefined) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parent = document.getElementById(elementToRender.parentElement.id);
          const firstFilter = parent?.querySelector('.js-filter');
          if (firstFilter) firstFilter.before(elementToRender);
          else if (parent) parent.append(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const current = document.querySelector(selector);
      if (activeFacetsElement && current) current.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const current = document.querySelector(selector);
      if (source && current) current.innerHTML = source.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}${window.location.hash}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = new URL(event.currentTarget.href, window.location.href);
    FacetFiltersForm.renderPage(url.search.slice(1));
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.formState = FacetFiltersForm.captureFormState();
FacetFiltersForm.appliedURL = `${window.location.pathname}${window.location.search}${window.location.hash}`;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
